import { createContext, useEffect, useState, ReactNode } from "react";
import { getAuth } from "./allauth";
import { captureException } from "@/posthog-analytics/posthog";

interface User {
  id: number;
  display: string;
  first_name: string;
  last_name: string;
  has_usable_password: boolean;
  email: string;
  username: string;
  tenant_roles: {
    [tenant: string]: {
      role: string;
      jobs: { [jobId: string]: string };
    };
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  refreshAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshAuth = async () => {
    try {
      const response = await getAuth();
      if (response.meta?.is_authenticated) {
        setUser((response.data as { user: User }).user);
      } else {
        setUser(null);
      }
    } catch (error) {
      captureException(error, {
        extra: {
          operation: 'refresh_auth',
          timestamp: Date.now()
        },
        tags: {
          component: 'AuthContext',
          operation: 'refreshAuth',
          module: 'auth',
        }
      });

      console.error("[AuthContext.refreshAuth] Error fetching auth:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Call getAuth() exactly once on page load
    refreshAuth();
  }, []);

  // While loading, you could render a spinner (or nothing) to avoid flashing the login UI
  if (loading) return null;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
